
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Hash, Users, MessageSquare } from 'lucide-react';

interface TrendAnalyticsProps {
  results?: any[];
}

const TrendAnalytics = ({ results = [] }: TrendAnalyticsProps) => {
  // Extract trending hashtags
  const hashtagCounts: { [key: string]: number } = {};
  results.forEach(result => {
    if (result.content?.hashtags) {
      result.content.hashtags.forEach((tag: string) => {
        const cleanTag = tag.replace('#', '');
        hashtagCounts[cleanTag] = (hashtagCounts[cleanTag] || 0) + 1;
      });
    }
  });

  const trendingHashtags = Object.entries(hashtagCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([tag, count]) => ({ tag, count }));

  // Platform distribution
  const platformCounts: { [key: string]: number } = {};
  results.forEach(result => {
    const platform = result.content?.platform || 'unknown';
    platformCounts[platform] = (platformCounts[platform] || 0) + 1;
  });

  const platformData = Object.entries(platformCounts).map(([platform, count]) => ({
    platform: platform.charAt(0).toUpperCase() + platform.slice(1),
    count
  }));

  // Engagement metrics over time (mock data for now)
  const engagementData = [
    { time: '00:00', likes: 120, shares: 45, comments: 23 },
    { time: '04:00', likes: 89, shares: 34, comments: 18 },
    { time: '08:00', likes: 234, shares: 89, comments: 67 },
    { time: '12:00', likes: 345, shares: 123, comments: 89 },
    { time: '16:00', likes: 567, shares: 234, comments: 145 },
    { time: '20:00', likes: 432, shares: 167, comments: 98 }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="w-full max-w-none space-y-6 overflow-hidden">
      {/* Trending Hashtags */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Hash className="h-5 w-5" />
            <span>Trending Hashtags</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trendingHashtags.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hashtags found in current results</p>
          ) : (
            <div className="space-y-3">
              {trendingHashtags.map((item, idx) => (
                <div key={item.tag} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <span className="font-medium">#{item.tag}</span>
                  </div>
                  <Badge variant="secondary">{item.count} mentions</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Platform Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Platform Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full h-64 min-h-[16rem]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ platform, percent }) => `${platform} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Engagement Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Engagement Trends</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full h-64 min-h-[16rem]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="likes" stackId="a" fill="#3B82F6" />
                <Bar dataKey="shares" stackId="a" fill="#10B981" />
                <Bar dataKey="comments" stackId="a" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top Keywords */}
      <Card>
        <CardHeader>
          <CardTitle>Top Keywords</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {['AI revolution', 'digital transformation', 'social commerce', 'customer service', 'sustainability'].map((keyword, idx) => (
              <div key={keyword} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <MessageSquare className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">{keyword}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">{Math.floor(Math.random() * 50) + 10} mentions</Badge>
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    +{Math.floor(Math.random() * 20) + 5}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Influencer Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Most Active Users</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {results.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No user activity data available</p>
          ) : (
            <div className="space-y-4">
              {results.slice(0, 5).map((result, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                      {(result.content?.author || result.author_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">@{result.content?.author || result.author_name || 'unknown'}</p>
                      <p className="text-sm text-gray-500">{result.content?.platform || result.platform || 'unknown'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{Math.floor(Math.random() * 10) + 1} posts</p>
                    <p className="text-sm text-gray-500">
                      {Math.floor(Math.random() * 1000) + 100} total engagement
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrendAnalytics;
